<template>
    <div>
        <!-- Settings Modal -->
        <transition name="fade">
            <div class="settings-modal-overlay" v-if="showSettings" @click.self="showSettings = false">
                <div class="settings-modal">
                    <div class="modal-header">
                        <h2>Slot Machine Settings</h2>
                        <button class="close-button" @click="showSettings = false">×</button>
                    </div>
                    <div class="modal-content">
                        <div class="setting-item">
                            <label for="spinDuration">Spin Duration ( {{ spinDuration }} seconds):</label>
                            <input
                                type="range" 
                                id="spinDuration" 
                                v-model.number="spinDuration" 
                                min="1" 
                                max="15"
                                @change="validateSpinDuration"
                            >
                        </div>
                        <div class="setting-item">
                            <label>
                                <input type="checkbox" v-model="enableSounds">
                                Enable Sound Effects
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button @click="saveSettings">Save Settings</button>
                    </div>
                </div>
            </div>
        </transition>

        <div class="slot-machine-container">
            <!-- Main slot machine -->
            <div class="main-content">
                <div class="slot-machine">
                    <div class="settings-button" @click="showSettings = true">
                        <i class="mdi mdi-cog"></i>
                    </div>
                    <div 
                        class="start-button" 
                        :class="{ 'start-button-disabled': entriesParticipants.length === 0 || (slotEntryData.winners?.length == slotEntryData.numberOfWinners) }"
                        @click="startSpin"
                        :disabled="entriesParticipants.length === 0"
                    >
                        <i class="mdi mdi-dice-multiple start-icon"></i>
                    </div>
                    <div class="machine-body">
                        <div class="right-arrow">▶</div>
                        <div class="slot-display">
                            <div class="slot-window">
                                <!-- Animated Loading Bar -->
                                <template v-if="loading">
                                    <div class="loading-container">
                                        <div class="loader"></div>
                                    </div>
                                </template>
                                <!-- No Entries -->
                                <template v-else-if="entriesParticipants.length === 0">
                                    <div class="no-entries">{{ $t('DialogTitle.Raffle.No_Entries') }}</div>
                                </template>
                                <template v-else>
                                    <div class="slot-reel" :style="reelStyle" ref="reel">
                                        <div v-if="spinning" class="entries-wrapper">
                                            <span class="text-black font-black text-xl name-item">{{ animatedWinnerName }}</span>
                                            <div class="ticket-item">
                                                {{ animatedTicketNumber }}
                                            </div>
                                        </div>
                                        <div v-for="(participant, index) in entriesParticipants" :key="participant.ticketID" class="entries-wrapper">
                                            <div class="name-item">
                                                {{ participant.username }}
                                            </div>
                                            <div class="ticket-item">
                                                {{ participant.ticketID }}
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="left-arrow">◀</div>
                    </div>
                </div>

                <div class="pt-2">
                    <p>{{ remainingSpins }} {{ remainingSpins === 1 ? 'spin' : 'spins' }} left</p>
                    <p class="spin-instruction">{{ $t('DialogTitle.Raffle.Click_The_Logo') }} <i class="mdi mdi-dice-multiple start-icon" style="color:black"></i> {{ $t('DialogTitle.Raffle.To_Spin') }}</p>
                </div>
                <div v-if="entriesParticipants.length > 0" class="winners-section">
                    <v-btn @click="showRaffleEntries(slotEntryData)" class="winners-text">View Entries/Winners</v-btn>
                    <div class="d-flex align-center">
                        <v-icon small class="mr-1">mdi-trophy</v-icon>
                        <span class="text-body-2"> {{ slotEntryData.winners?.length || 0  }} / {{ slotEntryData.numberOfWinners || 0 }} </span>
                    </div>
                </div>
            </div>

            <!-- Winner Modal -->
            <transition name="fade-top">
                <div class="modal-overlay" v-if="showWinnerModal" @click.self="closeModal">
                    <div class="confetti" v-if="showWinnerModal">
                        <span v-for="n in 50" :key="n" class="confetti-piece" :style="getRandomConfettiStyle(n)"></span>
                    </div>
                    <div class="winner-modal">
                        <div class="modal-header">
                            <h2>Raffle Winner</h2>
                            <button class="close-button" @click="closeModal">×</button>
                        </div>
                        <div class="modal-content">
                            <h1 class="winner-announcement">The winner is<br>{{ winner?.winnerDisplayname }} 🎉</h1>
                            <div class="winner-announcemen-ticket-id">{{ winner?.ticketId }}</div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
        <RaffleEntries ref="RaffleEntries"/>
    </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import RaffleEntries from '../components/Dialogs/Views/RaffleEntries.vue'

export default defineComponent({
    components: {
        RaffleEntries
    },
    props: {
        slotData: Object
    },
    data() {
        return {
            raffleId: null,
            slotEntryData: [],
            loading: true,
            entries: [],
            entriesParticipants: [],
            spinning: false,
            reelStyle: {},
            showWinnerModal: false,
            winner: {},
            animatedWinnerName: '',
            animatedTicketNumber: '',
            confettiColors: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722'],
            
            audioContext: null,
            spinSound: null,
            winSound: null,
            showSettings: false,
            spinDuration: 5,
            enableSounds: true,
            settingsLoaded: false
        }
    },
    async mounted() {
        this.raffleId = this.slotData._id;
        await this.loadSettings();
        await this.init();
        await this.initializeReel();
    },
    methods: {
        playWinSound() {
            if (!this.enableSounds) return;
            const winSound = new Audio('/sound_effects/winner.mp3');
            winSound.play().catch(error => console.error("Error playing sound:", error));
        },

        playSpinSound() {
            // Todo:
            if (!this.enableSounds) return;

        },

        async loadSettings() {
            const savedSettings = localStorage.getItem('slotMachineSettings');
            if (savedSettings) {
                try {
                    const settings = JSON.parse(savedSettings);
                    this.spinDuration = settings.spinDuration || 3;
                    this.enableSounds = settings.enableSounds !== false;
                } catch (e) {
                    console.error("Error loading settings:", e);
                }
            }
            this.settingsLoaded = true;
        },
        
        saveSettings() {
            const settings = {
                spinDuration: this.spinDuration,
                enableSounds: this.enableSounds
            };
            localStorage.setItem('slotMachineSettings', JSON.stringify(settings));
            this.showSettings = false;
        },
        
        validateSpinDuration() {
            if (this.spinDuration < 1) this.spinDuration = 1;
            if (this.spinDuration > 10) this.spinDuration = 10;
        },
        
        async init() {
            this.loading = true;
            const token = localStorage.getItem('maherco-admin-accesstoken');
            try {
                const res = await axios.get(
                    `${process.env.VUE_APP_SERVER_URL}/api/admin/raffles/${this.raffleId}`, 
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
                        },
                    }
                );
                
                if (res.data) {
                    this.slotEntryData = res.data.raffle;
                }
            } catch (error) {
                this.handleApiError(error);
            } finally {
                this.loading = false;
            }
        },
        
        async initializeReel() {
            this.loading = true;
            const token = localStorage.getItem('maherco-admin-accesstoken');
            try {
                const res = await axios.get(
                    `${process.env.VUE_APP_SERVER_URL}/api/admin/raffles/${this.raffleId}/entries`, 
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "x-admin-key": process.env.VUE_APP_X_ADMIN_KEY,
                        },
                    }
                );
                
                if (res.data?.entries) {
                    this.entries = res.data.entries;
                    this.entriesParticipants = this.entries.map(entry => ({
                        ticketID: entry.ticketId,
                        username: entry.user.username || entry.user.displayName
                    }));
                }
            } catch (error) {
                this.handleApiError(error);
            } finally {
                this.loading = false;
                this.startIdleScroll();
            }
        },
        
        handleApiError(error) {
            if (error.response) {
                if (error.response.status === 401) {
                    localStorage.removeItem("vuex");
                    localStorage.removeItem("maherco-admin-accesstoken");
                    window.location.reload();
                } else {
                    console.log(`Error: ${error.response.status}`);
                }
            } else if (error.request) {
                console.log('No response received', error.request);
            } else {
                console.log('Error', error.message);
            }
        },
        
        async startSpin() {
            if (this.spinning || this.entriesParticipants.length === 0) {
                alert("No Entries Yet" || "Already spinning...");
                return;
            }

            if (this.slotEntryData.winners?.length == this.slotEntryData?.numberOfWinners) {
                return;
            }

            this.spinning = true;
            this.animatedWinnerName = "";
            this.animatedTicketNumber = "";
            this.winner = {};
            this.showWinnerModal = false;

            // Start animation with placeholders
            const animationPromise = this.animateRollingRaffleEntries({
                winnerDisplayname: "???????",
                ticketId: "TICKET-?????????????-??????"
            });

            const token = localStorage.getItem('maherco-admin-accesstoken');
            try {
                const res = await axios.post(
                    `${process.env.VUE_APP_SERVER_URL}/api/admin/raffles/${this.raffleId}/draw`, 
                    [], 
                    { 
                        headers: { 
                            'Authorization': `Bearer ${token}`,
                            'x-admin-key': process.env.VUE_APP_X_ADMIN_KEY,
                        }
                    }
                );

                if (res.data?.winner) {
                    const winnerDisplayname = res.data.winner?.user.displayName;
                    const ticketId = res.data.winner?.ticketId;
                    this.winner = { winnerDisplayname, ticketId };

                    // Wait for animation to finish, then set real values
                    await animationPromise;
                    this.animatedWinnerName = winnerDisplayname;
                    this.animatedTicketNumber = ticketId;
                    this.playWinSound();
                } else {
                    throw new Error("No winner received from API");
                }
            } catch (error) {
                this.spinning = false;
                console.error("Error drawing winner:", error);
                
                if (error.response) {
                    if (error.response.status === 401) {
                        localStorage.removeItem("vuex");
                        localStorage.removeItem("maherco-admin-accesstoken");
                        window.location.reload();
                    } else {
                        alert(`Error: ${error.response.status}. ${error.message}`);
                    }
                } else if (error.request) {
                    alert("No response from server. Please try again.");
                } else {
                    alert("An unexpected error occurred.");
                }
            }
        },

        animateRollingRaffleEntries(data) {
            return new Promise((resolve) => {
                let counter = 0;
                const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
                const ticketCharacters = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                const totalIterations = Math.max(10, Math.floor(this.spinDuration * 1000 / 150));

                const generateRandomString = (length) => 
                    Array.from({ length }, () => characters[Math.floor(Math.random() * characters.length)]).join("");

                const generateRandomTicket = () => 
                    `TICKET-${Array.from({ length: 13 }, () => ticketCharacters[Math.floor(Math.random() * ticketCharacters.length)]).join("")}-${generateRandomString(6)}`;

                this.usernameAnimationInterval = setInterval(() => {
                    if (counter >= totalIterations) {
                        clearInterval(this.usernameAnimationInterval);
                        this.animatedWinnerName = data.winnerDisplayname;
                        this.animatedTicketNumber = data.ticketId;
                        this.spinning = false;
                        this.showWinnerModal = true;
                        this.playWinSound(); // Play win sound when showing winner
                        resolve();
                    } else {
                        this.animatedWinnerName = generateRandomString(8);
                        this.animatedTicketNumber = generateRandomTicket();
                        counter++;
                    }
                }, 150);
            });
        },
        
        async closeModal() {
            this.showWinnerModal = false;
            await this.init();
        },
        
        getRandomConfettiStyle(index) {
            const left = Math.random() * 100;
            const animationDelay = Math.random() * 3;
            const color = this.confettiColors[index % this.confettiColors.length];
            
            return {
                left: `${left}%`,
                backgroundColor: color,
                animationDelay: `${animationDelay}s`
            };
        },
        
        startIdleScroll() {
            if (this.entriesParticipants.length > 1) {
                setInterval(() => {
                    if (!this.spinning) {
                        this.reelStyle = { transform: 'translateY(-75px)', transition: '.5s ease-in-out' };
                        setTimeout(() => {
                            this.entriesParticipants.push(this.entriesParticipants.shift());
                            this.reelStyle = { transform: 'translateY(0)', transition: 'none' };
                        }, 500);
                    }
                }, 2000);
            }
        },
        
        showRaffleEntries(data) {
            this.$refs.RaffleEntries.initView(data);
        },
    },
    computed: {
        remainingSpins() {
            return (this.slotEntryData.numberOfWinners || 0) - (this.slotEntryData.winners?.length || 0);
        }
    }
});
</script>

<style scoped>
    /* Loading Animation */
    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 60px;
    }

    .loading-bar {
        width: 50px;
        height: 10px;
        background: linear-gradient(90deg, #2196F3, #00BCD4);
        border-radius: 5px;
        animation: loading-animation 1.5s infinite;
    }

    @keyframes loading-animation {
        0% {
            width: 0;
            opacity: 0.5;
        }
        50% {
            width: 50px;
            opacity: 1;
        }
        100% {
            width: 0;
            opacity: 0.5;
        }
    }
    .slot-machine-container {
        font-family: Arial, sans-serif;
        max-width: 1200px;
        margin: 0 auto;
        padding: 20px;
        background-color: white;
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .top-nav {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 40px;
    }
    .sound-button {
        background-color: #ff5722;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
    }

    .right-controls {
        display: flex;
        gap: 20px;
        align-items: center;
    }

    .volume-icon, .help-icon, .settings-icon {
        cursor: pointer;
        font-size: 20px;
    }

    .main-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        width: 100%;
    }
    
    .spin-button-container {
        margin-bottom: 20px;
    }
    
    .spin-button {
        background-color: #ff5722;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
    }
    
    .slot-machine {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    
    .start-button {
        width: 70px;
        height: 70px;
        border-radius: 50%;
        background: linear-gradient(135deg, #ff5722, #ff9800);
        box-shadow: 0 4px 15px rgba(255, 87, 34, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: -40px;
        cursor: pointer;
        z-index: 2;
        position: relative;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .start-button-disabled {
        width: 70px;
        height: 70px;
        border-radius: 50%;
        background: linear-gradient(135deg, #b0b0b0, #d3d3d3); /* Gray out the button */
        box-shadow: none; /* Remove shadow to indicate disabled */
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: -40px;
        cursor: not-allowed; /* Change cursor to indicate it's disabled */
        z-index: 2;
        position: relative;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .no-entries {
        font-size: 30px;
        font-weight: bold;
        color: gray;
        text-align: center;
        padding: 20px;
    }

    .start-button:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 20px rgba(255, 87, 34, 0.7);
    }

    .start-icon {
        font-size: 32px;
        color: white;
    }
    
    .machine-body {
        background-color: #661f0a;
        border: 15px solid #a0311b;
        border-radius: 30px;
        padding: 0;
        display: flex;
        align-items: center;
        width: 700px;
        height: 200px;
        position: relative;
        overflow: hidden;
    }
    
    .machine-body::before, .machine-body::after {
        content: '';
        position: absolute;
        top: -15px;
        bottom: -15px;
        width: 60px;
        background-color: #ff5722;
        z-index: -1;
    }
    
    .machine-body::before {
        left: -15px;
        border-top-left-radius: 30px;
        border-bottom-left-radius: 30px;
    }
    
    .machine-body::after {
        right: -15px;
        border-top-right-radius: 30px;
        border-bottom-right-radius: 30px;
    }
    
    .left-arrow, .right-arrow {
        font-size: 50px;
        color: #ffc107;
        z-index: 2;
    }
    .entries-wrapper {
        padding: 8px
    }
    .slot-display {
        flex: 1;
        background-color: white;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border-radius: 10px;
        overflow: hidden;
    }
    
    .slot-window {
        height: 80px;
        overflow: hidden;
        position: relative;
        width: 100%;
    }
    
    .slot-reel {
        position: absolute;
        width: 100%;
        text-align: center;
    }
    
    .ticket-item {
        padding-top: 20px;
        height: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        font-weight: bold;
        font-family: "Lucida Console", "Courier New", monospace;
        color: #010415;
    }

    .name-item {
        height: 40px;
        padding-bottom: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 45px;
        font-weight: bold;
        color: #684242;
    }
    
    .winners-section {
        margin-top: 60px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    
    .winners-button {
        background-color: #ff5722;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        margin-bottom: 10px;
    }
    
    .winners-text {
        color: #ffc107;
        font-weight: bold;
        margin-bottom: 5px;
    }
    
    .spin-instruction {
        color: #999;
        font-size: 14px;
    }
    
    /* Modal styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
    }
    
    .winner-modal {
        background-color: white;
        border-radius: 8px;
        width: 400px;
        position: relative;
        overflow: hidden;
    }
    
    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 20px;
        border-bottom: 1px solid #eee;
    }
    
    .modal-header h2 {
        margin: 0;
        color: #ffc107;
    }
    
    .close-button {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #999;
    }
    
    .modal-content {
        padding: 30px 20px;
        text-align: center;
    }
    
    .winner-announcement {
        font-size: 28px;
        margin-bottom: 20px;
    }
    .winner-announcemen-ticket-id {
        font-size: 20px;
        font-weight: bold;
        font-family: "Lucida Console", "Courier New", monospace;
    }
    
    .remove-link {
        color: #ff5722;
        text-decoration: none;
    }
    
    .remove-link:hover {
        text-decoration: underline;
    }
    
    /* Confetti animation */
    .confetti {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        overflow: hidden;
    }
    
    .confetti-piece {
        position: absolute;
        width: 10px;
        height: 10px;
        background-color: red;
        top: -10px;
        animation: fall 3s linear infinite;
    }
    
    @keyframes fall {
        0% {
        transform: translateY(0) rotate(0deg);
        opacity: 1;
        }
        100% {
        transform: translateY(100vh) rotate(720deg);
        opacity: 0;
        }
    }

    .fade-top-enter-active, .fade-top-leave-active {
        transition: opacity 0.3s ease, transform 0.3s ease;
    }
    .fade-top-enter-from, .fade-top-leave-to {
        opacity: 0;
        transform: translateY(-30px);
    }
    .fade-top-enter-to, .fade-top-leave-from {
        opacity: 1;
        transform: translateY(0);
    }

    /* HTML: <div class="loader"></div> */
    .loader {
        width: 120px;
        height: 20px;
        background: 
            linear-gradient(90deg, #0000 ,orange) left -50px top 0/50px 20px no-repeat 
            lightblue;
        animation: l2 1s infinite linear;
    }
    @keyframes l2 {
        100% {background-position: right -50px top 0}
    }

    .settings-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
    }

    .settings-modal {
        background-color: white;
        border-radius: 8px;
        padding: 20px;
        width: 400px;
        max-width: 90%;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        position: relative;
    }

    .settings-modal .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .settings-modal .modal-content {
        margin-bottom: 20px;
    }

    .setting-item {
        margin-bottom: 15px;
    }

    .setting-item label {
        display: block;
        margin-bottom: 5px;
        font-weight: 500;
    }

    .setting-item input[type="number"] {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
    }

    .setting-item input[type="checkbox"] {
        margin-right: 8px;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
    }

    .modal-footer button {
        padding: 8px 16px;
        background-color: #4CAF50;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
    }

    .modal-footer button:hover {
        background-color: #45a049;
    }

    .settings-button {
        position: absolute;
        top: 10px;
        right: 10px;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        z-index: 10;
        transition: all 0.2s;
    }

    .settings-button:hover {
        background: rgba(255, 255, 255, 1);
        transform: rotate(90deg);
    }

    .settings-button i {
        font-size: 20px;
        color: #333;
    }

</style>